import React, { useEffect, useRef, type ReactElement } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type {
  RdScheduleAppointment,
  RdScheduleWeek,
} from "../../../../shared/rd-schedule";
import type { AppointmentActionDialogsController } from "./appointment-action-dialogs";
import { ConnectedAppointmentDetailsPanel } from "./connected-appointment-details-panel";
import { detailsActions } from "./week-action-mapping";
import { refreshAppointmentReads } from "./appointment-reads";
import { useScheduleApi } from "./actions/use-action-submit";
import { schedulePath } from "../../lib/rd-schedule-actions";

// NO-876. The Week view's side of the appointment dialogs: the details panel
// (NO-875) for the clicked block, whose footer opens Reschedule, Cancel or mark
// missed and Delete, and whose Note section opens Edit note. The route owns the
// controller and the selected block so the header button (outside the panel)
// opens the same dialogs; the dialogs themselves mount at page level (NO-912).

export function WeekAppointmentActions(props: {
  week: RdScheduleWeek;
  selectedId: string | null;
  onSelect: (appointmentId: string | null) => void;
  controller: AppointmentActionDialogsController;
  onOpenVisitPage: (appointmentId: string) => void;
  /** The RD a supervisor or admin acts for; null for an RD acting on themself. */
  rdId?: string | null;
}): ReactElement {
  const { week, selectedId, onSelect, controller } = props;
  const reset = useRef(() => {});
  const api = useScheduleApi();
  const queryClient = useQueryClient();

  // NO-917: send a failed copy to Healthie again through the action's retry route.
  const retryMirror = async (visit: RdScheduleAppointment): Promise<void> => {
    if (!visit.mirrorActionId) return;

    await api(schedulePath(`/actions/${visit.mirrorActionId}/retry`, props.rdId), {
      method: "POST",
    });
    refreshAppointmentReads(queryClient);
  };

  reset.current = () => {
    controller.setRequest(null);
    onSelect(null);
  };

  // Leaving the Week view must not leave a request behind to reopen later.
  useEffect(() => () => reset.current(), []);
  const appointment = week.appointments.find(({ id }) => id === selectedId) ?? null;

  return (
    <ConnectedAppointmentDetailsPanel
      rdId={props.rdId}
      appointment={appointment}
      timezone={week.timezone}
      open={appointment !== null}
      onOpenChange={(open) => {
        if (!open) onSelect(null);
      }}
      actions={{
        ...detailsActions({
          controller,
          onClosePanel: () => onSelect(null),
          onOpenVisitPage: props.onOpenVisitPage,
        }),
        onRetryMirror: retryMirror,
      }}
    />
  );
}
